import { writable, derived } from 'svelte/store';
import {
	BannerApiClassEnum,
	BannerClassStatusEnum,
	BannerIconStatusEnum,
	BannerStyleStatusEnum
} from '../interfaces/BannerStatusEnum';
import type { AdminBanner } from '../interfaces/AdminBanner';
import { BannerService } from '../domain-services/Banner.service';

const bannerService = new BannerService();

function createBannerStore() {
	const { subscribe, set, update } = writable<AdminBanner[]>([]);

	// Explicitly deactivates the currently active banner (other than excludedId) via a PUT call
	async function deactivateCurrentlyActiveBanner(
		banners: AdminBanner[],
		excludedId?: string
	): Promise<AdminBanner | undefined> {
		const currentlyActive = banners.find((b) => b.isActive && b.id !== excludedId);
		if (!currentlyActive) return undefined;

		return bannerService.updateBanner(currentlyActive.id, {
			title: currentlyActive.title,
			content: currentlyActive.content,
			type: currentlyActive.type,
			isActive: false
		});
	}

	return {
		subscribe,

		fetchBanners: async (notifyOnError = true) => {
			const banners = await bannerService.getBanners(notifyOnError);
			set(banners);
		},

		togglePublish: async (id: string) => {
			let currentBanners: AdminBanner[] = [];
			update((banners) => {
				currentBanners = banners;
				return banners;
			});
			const target = currentBanners.find((b) => b.id === id);
			if (!target) return;

			const willBeActive = !target.isActive;
			const deactivatedBanner = willBeActive
				? await deactivateCurrentlyActiveBanner(currentBanners, id)
				: undefined;

			const updatedBanner = await bannerService.updateBanner(id, {
				title: target.title,
				content: target.content,
				type: target.type,
				isActive: willBeActive
			});

			update((banners) =>
				banners.map((b) => {
					if (b.id === updatedBanner.id) return updatedBanner;
					if (b.id === deactivatedBanner?.id) return deactivatedBanner;
					return b;
				})
			);
		},

		deleteBanner: async (id: string) => {
			await bannerService.deleteBanner(id);
			update((banners) => banners.filter((b) => b.id !== id));
		},

		saveBanner: async (banner: Partial<AdminBanner>) => {
			let currentBanners: AdminBanner[] = [];
			update((banners) => {
				currentBanners = banners;
				return banners;
			});

			const willBeActive = banner.isActive ?? false;
			const deactivatedBanner = willBeActive
				? await deactivateCurrentlyActiveBanner(currentBanners, banner.id)
				: undefined;

			if (banner.id) {
				const updatedBanner = await bannerService.updateBanner(banner.id, {
					title: banner.title ?? '',
					content: banner.content ?? '',
					type: banner.type ?? BannerApiClassEnum.Information,
					isActive: willBeActive
				});
				update((banners) =>
					banners.map((b) => {
						if (b.id === updatedBanner.id) return updatedBanner;
						if (b.id === deactivatedBanner?.id) return deactivatedBanner;
						return b;
					})
				);
			} else {
				const createdBanner = await bannerService.createBanner({
					title: banner.title ?? '',
					content: banner.content ?? '',
					type: banner.type ?? BannerApiClassEnum.Information,
					isActive: willBeActive
				});
				update((banners) => {
					const nextBanners = deactivatedBanner
						? banners.map((b) => (b.id === deactivatedBanner.id ? deactivatedBanner : b))
						: banners;
					return [...nextBanners, createdBanner];
				});
			}
		}
	};
}

export const bannerStore = createBannerStore();

export const bannerDisplayInfos: Record<
	BannerApiClassEnum,
	{ class: string; iconClass: string; style: string }
> = {
	[BannerApiClassEnum.Information]: {
		class: BannerClassStatusEnum.Information,
		iconClass: BannerIconStatusEnum.Information,
		style: BannerStyleStatusEnum.Information
	},
	[BannerApiClassEnum.Warning]: {
		class: BannerClassStatusEnum.Warning,
		iconClass: BannerIconStatusEnum.Warning,
		style: BannerStyleStatusEnum.Warning
	},
	[BannerApiClassEnum.Danger]: {
		class: BannerClassStatusEnum.Danger,
		iconClass: BannerIconStatusEnum.Danger,
		style: BannerStyleStatusEnum.Danger
	},
	[BannerApiClassEnum.Success]: {
		class: BannerClassStatusEnum.Success,
		iconClass: BannerIconStatusEnum.Success,
		style: BannerStyleStatusEnum.Success
	}
};

// Translation keys (src/lang/*.json) for each banner type, keyed by the API's `type` value
export const bannerTypeLabels: Record<BannerApiClassEnum, string> = {
	[BannerApiClassEnum.Information]: 'banner_type_information',
	[BannerApiClassEnum.Warning]: 'banner_type_warning',
	[BannerApiClassEnum.Danger]: 'banner_type_danger',
	[BannerApiClassEnum.Success]: 'banner_type_success'
};

export const activeBanner = derived(bannerStore, ($banners) => {
	const banner = $banners.find((b) => b.isActive);
	if (!banner) return null;

	return {
		...banner,
		messagePrincipal: banner.content,
		...bannerDisplayInfos[banner.type]
	};
});
